import React, { useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { X, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { parseSimpleMarkdown } from '@/utils/formatters';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'progress';
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'top-center'
  | 'bottom-right'
  | 'bottom-left'
  | 'bottom-center';

export interface ToastNotificationProps {
  title?: string;
  message?: string | { title?: string; description?: string };
  description?: string;
  type?: ToastType;
  duration?: number;
  onClose?: () => void;
  onClick?: () => void;
  autoClose?: boolean;
  position?: ToastPosition;
  progress?: number;
  stackIndex?: number;
}

/**
 * ToastNotification - Componente de notificação toast
 *
 * Estilo moderno com:
 * - Fundo escuro com gradiente da cor do tipo
 * - Ícone grande em círculo colorido
 * - Título colorido + descrição em cinza claro
 *
 * @param {string} title - Título da notificação (colorido)
 * @param {string} message - Mensagem/descrição (ou use description)
 * @param {string} description - Descrição adicional (cinza claro)
 * @param {'success'|'error'|'warning'|'info'} type - Tipo da notificação
 * @param {number} duration - Duração em ms (default: 4000)
 * @param {function} onClose - Callback ao fechar
 * @param {boolean} autoClose - Se fecha automaticamente (default: true)
 * @param {string} position - Posição na tela (default: 'top-right')
 */
const ToastNotification = ({
  title,
  message,
  description,
  type = 'info',
  duration = 4000,
  onClose,
  onClick,
  autoClose = true,
  position = 'top-right',
  progress,
  stackIndex = 0
}: ToastNotificationProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  // Narrow object-shaped message to access legacy .title/.description fields
  const messageAsObject =
    message && typeof message === 'object' ? message : undefined;

  // Compatibilidade: se só passou message (string simples), usa como description
  const displayTitle = title || (typeof message === 'string' && !description ? null : messageAsObject?.title);
  const displayDescription = description || (typeof message === 'string' ? message : messageAsObject?.description);

  useEffect(() => {
    if (autoClose && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoClose, duration]);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  };

  if (!isVisible) return null;

  const getIcon = () => {
    const iconProps = { size: 18, strokeWidth: 2.5 }; // Mesmo tamanho do X
    switch (type) {
      case 'success':
        return <CheckCircle {...iconProps} />;
      case 'error':
        return <X {...iconProps} />;
      case 'warning':
        return <AlertTriangle {...iconProps} />;
      case 'progress':
        return (
          <div style={{
            width: 18,
            height: 18,
            border: '2.5px solid rgba(255,255,255,0.3)',
            borderTop: '2.5px solid #ffffff',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
        );
      default:
        return <Info {...iconProps} />;
    }
  };

  // Cores para cada tipo - baseado nos modelos de referência
  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          gradient: 'rgba(34, 197, 94, 0.45)',   // Verde 45% opacidade
          iconBg: '#22c55e',                      // Green
          iconColor: '#1f2937',                   // Cinza bem escuro (gray-800)
          title: '#4ade80',                       // Verde neon brilhante
          description: '#cbd5e1'                  // Slate-300 (mais claro)
        };
      case 'error':
        return {
          gradient: 'rgba(239, 68, 68, 0.45)',   // Vermelho 45% opacidade
          iconBg: '#ef4444',                      // Red
          iconColor: '#1f2937',                   // Cinza bem escuro (gray-800)
          title: '#f87171',                       // Vermelho neon
          description: '#cbd5e1'
        };
      case 'warning':
        return {
          gradient: 'rgba(245, 158, 11, 0.45)',  // Amber 45% opacidade
          iconBg: '#f59e0b',                      // Amber
          iconColor: '#1f2937',                   // Cinza bem escuro (gray-800)
          title: '#fbbf24',                       // Amber neon
          description: '#cbd5e1'
        };
      case 'progress':
        return {
          gradient: 'rgba(99, 102, 241, 0.45)',
          iconBg: '#6366f1',
          iconColor: '#1f2937',
          title: '#a5b4fc',
          description: '#cbd5e1'
        };
      default: // info
        return {
          gradient: 'rgba(59, 130, 246, 0.45)',  // Blue 45% opacidade
          iconBg: '#3b82f6',                      // Blue
          iconColor: '#1f2937',                   // Cinza bem escuro (gray-800)
          title: '#60a5fa',                       // Blue neon
          description: '#cbd5e1'
        };
    }
  };

  const colors = getColors();

  const getPositionStyles = (): CSSProperties => {
    const base: CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      maxWidth: '500px',   // +20% (era 420px)
      minWidth: '380px'    // +20% (era 320px)
    };

    const offset = stackIndex * 90;

    switch (position) {
      case 'top-left':
        return { ...base, top: `${60 + offset}px`, left: '20px' };
      case 'top-center':
        return { ...base, top: `${60 + offset}px`, left: '50%', transform: isAnimating ? 'translateX(-50%) translateY(-20px)' : 'translateX(-50%)' };
      case 'top-right':
        return { ...base, top: `${60 + offset}px`, right: '20px' };
      case 'bottom-left':
        return { ...base, bottom: `${20 + offset}px`, left: '20px' };
      case 'bottom-center':
        return { ...base, bottom: `${20 + offset}px`, left: '50%', transform: isAnimating ? 'translateX(-50%) translateY(20px)' : 'translateX(-50%)' };
      case 'bottom-right':
        return { ...base, bottom: `${20 + offset}px`, right: '20px' };
      default:
        return { ...base, top: `${60 + offset}px`, right: '20px' };
    }
  };

  // Renderizar texto com suporte a **negrito**
  const renderMarkdown = (text: string) => {
    const segments = parseSimpleMarkdown(text);
    if (!segments) return text;
    return segments.map((seg, i) =>
      typeof seg === 'string' ? seg : <strong key={i}>{seg.text}</strong>
    );
  };

  // Background com gradiente
  const backgroundStyle: CSSProperties = {
    background: `linear-gradient(to right, ${colors.gradient} 0%, rgba(30, 41, 59, 0.95) 20%, rgba(30, 41, 59, 0.95) 100%)`,
    backdropFilter: 'blur(12px)'
  };

  const containerStyle: CSSProperties = {
    ...getPositionStyles(),
    ...backgroundStyle,
    overflow: 'hidden',
    borderRadius: '16px',
    padding: '16px 20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    opacity: isAnimating ? 0 : 1,
    transform: isAnimating
      ? (position.includes('right') ? 'translateX(100%)' : position.includes('left') ? 'translateX(-100%)' : undefined)
      : 'translateX(0)',
    transition: 'all 0.3s ease-in-out',
    animation: isVisible && !isAnimating ? 'toastSlideIn 0.3s ease-out' : undefined,
  };

  return (
    <div style={containerStyle}>
      {/* Ícone em círculo - opacidade 100% independente do toast */}
      <div style={{
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: colors.iconBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: colors.iconColor,
        opacity: 1,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
      }}>
        {getIcon()}
      </div>

      {/* Conteúdo - clicável se onClick definido */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          cursor: onClick ? 'pointer' : 'default'
        }}
        onClick={onClick ? (e) => {
          e.stopPropagation();
          onClick();
          handleClose();
        } : undefined}
      >
        {displayTitle && (
          <div style={{
            color: colors.iconBg,  // Mesma cor do ícone (vermelho para error, verde para success, etc.)
            fontSize: '15px',
            fontWeight: '700',     // Negrito
            lineHeight: '1.4',
            marginBottom: displayDescription ? '6px' : 0,
            display: 'block'       // Forçar bloco para garantir linha separada
          }}>
            {renderMarkdown(displayTitle)}
          </div>
        )}
        {displayDescription && (
          <div style={{
            color: colors.description,
            fontSize: '14px',
            lineHeight: '1.4',
            fontWeight: '400',
            whiteSpace: 'pre-wrap'
          }}>
            {renderMarkdown(displayDescription)}
          </div>
        )}
        {/* Fallback: se não tem título nem description, mostra message como título */}
        {!displayTitle && !displayDescription && message && (
          <div style={{
            color: colors.title,
            fontSize: '15px',
            fontWeight: '600',
            lineHeight: '1.4'
          }}>
            {typeof message === 'string' ? renderMarkdown(message) : JSON.stringify(message)}
          </div>
        )}
      </div>

      {/* Barra de progresso */}
      {typeof progress === 'number' && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '0 0 16px 16px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${Math.min(progress, 100)}%`,
            background: colors.iconBg,
            transition: 'width 0.3s ease',
            borderRadius: '0 0 16px 16px'
          }} />
        </div>
      )}

      {/* Botão fechar */}
      <button
        onClick={handleClose}
        style={{
          background: 'transparent',  // Sem fundo
          border: 'none',
          color: '#94a3b8',
          cursor: 'pointer',
          padding: '6px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = '#ffffff';
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = '#94a3b8';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <X size={18} />
      </button>

      <style>{`
        @keyframes toastSlideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ToastNotification;
